import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as signalR from '@microsoft/signalr';

interface ConnectionStatusProps {
    connection: signalR.HubConnection;
    /**
     * 'dot' — точка с подписью в шапке списка дронов;
     * 'banner' — полоса под шапкой приложения, только при обрыве.
     *
     * Половинки разнесены намеренно: точка живёт внутри панели списка и
     * пропадает вместе с ней (FPV-режим, мобильный с выбранным дроном), а
     * баннер монтируется в Console и держится всегда — узнать об обрыве
     * важнее всего как раз тогда, когда список скрыт.
     */
    variant?: 'dot' | 'banner';
}

type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';

// Как часто сверяемся с состоянием соединения. Состояние читается опросом:
// событий хаба недостаточно (на первое успешное подключение он ничего не шлёт),
// а снять их с общего соединения потом нечем.
const SYNC_INTERVAL_MS = 1000;

function toState(state: signalR.HubConnectionState): ConnectionState {
    switch (state) {
        case signalR.HubConnectionState.Connected:
            return 'connected';
        case signalR.HubConnectionState.Connecting:
        case signalR.HubConnectionState.Reconnecting:
            return 'reconnecting';
        default:
            return 'disconnected';
    }
}

/**
 * Индикация связи с сервером.
 *
 * Вешается на соединение из Console: оно живёт всё время, пока открыта
 * консоль. Остальные соединения к /api/drone-hub (их по одному на каждый вызов
 * useDroneHubConnection) для индикатора не используются — рвутся они всё равно
 * вместе, а следить за одним достаточно.
 */
function ConnectionStatus({ connection, variant = 'dot' }: ConnectionStatusProps): ReactNode {
    const { t } = useTranslation(['console']);
    const [state, setState] = useState<ConnectionState>(() => toState(connection.state));

    const sync = useCallback((): void => {
        setState(toState(connection.state));
    }, [connection]);

    useEffect(() => {
        // Только опрос. У onreconnecting/onreconnected/onclose нет парного
        // «снять обработчик», а соединение теперь одно на всё приложение и
        // переживает размонтирование компонента — повешенное на него
        // копилось бы при каждом новом монтировании (FPV, мобильный вид).
        // Секунды задержки для индикатора связи достаточно.
        sync();
        const timer = window.setInterval(sync, SYNC_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [connection, sync]);

    const label = state === 'connected'
        ? t('connection_connected')
        : state === 'reconnecting'
            ? t('connection_reconnecting')
            : t('connection_disconnected');

    if (variant === 'banner') {
        if (state === 'connected') {
            return null;
        }

        return (
            <div className='connection-banner' role='alert'>
                {state === 'reconnecting' ? t('connection_banner') : t('connection_disconnected')}
            </div>
        );
    }

    return (
        <div className='connection-status' role='status'>
            <span className={'connection-dot connection-dot-' + state} aria-hidden='true' />
            <span className='connection-status-label'>{label}</span>
        </div>
    );
}

export default ConnectionStatus;
